import { type FC, type ReactNode, useEffect, useMemo, useState } from "react";
import { useIsViewerJourney } from "../../screens/viewer-feed/viewer-journey.context";
import PageTabs from "../page-tabs/page-tabs";
import type { PageTabsProps } from "../page-tabs/page-tabs.types";
import * as CSS from "./card.styles";

type CardProps = {
	children?: ReactNode;
	title?: string;
	header?: ReactNode;
	pages?: PageTabsProps[];
	borderColour?: string;
};

const Card: FC<CardProps> = ({
	children,
	title,
	header,
	pages,
	borderColour = "#e2e2e2",
}) => {
	const [activePage, setActivePage] = useState<string | null>(null);
	const isViewer = useIsViewerJourney();

	useEffect(() => {
		if (!pages?.length) return;

		setActivePage((current) => {
			if (current && pages.some((page) => page.id === current)) {
				return current;
			}
			return pages[0].id;
		});
	}, [pages]);

	const activePageContent = useMemo(() => {
		if (pages?.length && activePage !== null) {
			const foundContent = pages.find((page) => page.id === activePage);
			return foundContent?.children;
		}
	}, [pages, activePage]);

	if (pages?.length) {
		return (
			<CSS.Card $isViewer={isViewer} $borderColour={borderColour}>
				<CSS.StickyHeader $isViewer={isViewer}>
					<PageTabs
						pages={pages}
						activeId={activePage ?? ""}
						onClick={(id) => setActivePage(id)}
					/>
				</CSS.StickyHeader>
				<CSS.ScrollBody>{activePageContent}</CSS.ScrollBody>
			</CSS.Card>
		);
	}

	return (
		<CSS.Card $isViewer={isViewer} $borderColour={borderColour}>
			<CSS.StickyHeader $isViewer={isViewer}>
				{title && <CSS.Title>{title}</CSS.Title>}
				{header}
			</CSS.StickyHeader>
			{children && <CSS.ScrollBody>{children}</CSS.ScrollBody>}
		</CSS.Card>
	);
};

export default Card;
